import { MessageBus } from '@bs/utils';
import { CLASS_NAMES, EVENTS, MISC, SELECTORS } from '@bs/constants';

/**
 * A class for the Body Video module.
 *
 * Behaviour:
 *  - Clicking or pressing Enter/Space on the play button hides the trigger,
 *    fades out the thumbnail preview, enables native video controls,
 *    and starts playback.
 *  - On fine-pointer devices the native cursor is replaced by a circle that
 *    follows the pointer across the thumbnail. Position is written to the
 *    thumbnail as custom properties and consumed by a transform, so tracking
 *    never triggers layout. The circle retires once playback begins.
 */
export default class BodyVideo extends MessageBus {
  constructor(element) {
    super();

    this.element = element;

    // Whether playback has started — the cursor stands down once it has
    this.isPlaying = false;

    // Whether the pointer listeners were attached (fine pointers only)
    this.cursorEnabled = false;

    this.init();
  }

  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  cacheDomReferences() {
    this.cookieSettingsButton = this.element.querySelector(
      SELECTORS.BODY_VIDEO_COOKIE_SETTINGS
    );

    this.playButton = this.element.querySelector(SELECTORS.BODY_VIDEO_PLAY);

    this.video = this.element.querySelector(SELECTORS.BODY_VIDEO_VIDEO);
    this.iframe = this.element.querySelector(SELECTORS.BODY_VIDEO_IFRAME);

    this.thumbnail = this.element.querySelector(SELECTORS.BODY_VIDEO_THUMB);

    this.preview = this.element.querySelector(SELECTORS.BODY_VIDEO_PREVIEW);

    this.cursor = this.element.querySelector(SELECTORS.BODY_VIDEO_CURSOR);

    return this;
  }

  setupHandlers() {
    this.handleCookieSettingsClick = this.onCookieSettingsClick.bind(this);
    this.handlePlayClick = this.onPlay.bind(this);
    this.handlePlayKeydown = this.onPlayKeydown.bind(this);
    this.handlePointerEnter = this.onPointerEnter.bind(this);
    this.handlePointerLeave = this.onPointerLeave.bind(this);
    this.handlePointerMove = this.onPointerMove.bind(this);

    return this;
  }

  enable() {
    if (this.cookieSettingsButton) {
      this.cookieSettingsButton.addEventListener(
        EVENTS.CLICK,
        this.handleCookieSettingsClick
      );
    }

    if (!this.playButton || (!this.video && !this.iframe)) return this;

    this.playButton.addEventListener(EVENTS.CLICK, this.handlePlayClick);

    this.playButton.addEventListener(EVENTS.KEY_DOWN, this.handlePlayKeydown);

    this.enableCursor();

    return this;
  }

  /**
   * Reopens Cookiebot so the visitor can grant marketing consent.
   * @param {Event} event - The cookie settings button click event.
   * @returns {BodyVideo} A reference to the current instance
   * @chainable
   */
  onCookieSettingsClick(event) {
    event.preventDefault();

    if (window.Cookiebot && typeof window.Cookiebot.renew === 'function') {
      window.Cookiebot.renew();
    }

    return this;
  }

  /**
   * Attaches the pointer listeners, but only where a real pointer exists — on
   * touch there is nothing to follow and the circle would sit stranded.
   * @returns {BodyVideo} A reference to the current instance
   * @chainable
   */
  enableCursor() {
    if (!this.thumbnail || !this.cursor) return this;

    if (!window.matchMedia(MISC.FINE_POINTER).matches) return this;

    this.cursorEnabled = true;

    this.thumbnail.addEventListener(
      EVENTS.POINTERENTER,
      this.handlePointerEnter
    );

    this.thumbnail.addEventListener(
      EVENTS.POINTERLEAVE,
      this.handlePointerLeave
    );

    this.thumbnail.addEventListener(EVENTS.POINTERMOVE, this.handlePointerMove);

    return this;
  }

  /**
   * Reveals the circle, seeding its position from the entry point so it fades
   * in where the pointer already is rather than sliding in from the corner.
   * @param {PointerEvent} event - The pointerenter event on the thumbnail.
   * @returns {BodyVideo} A reference to the current instance
   * @chainable
   */
  onPointerEnter(event) {
    if (this.isPlaying) return this;

    this.setCursorPosition(event);

    this.thumbnail.classList.add(CLASS_NAMES.IS_CURSOR_ACTIVE);

    return this;
  }

  /**
   * Hides the circle when the pointer leaves the thumbnail.
   * @returns {BodyVideo} A reference to the current instance
   * @chainable
   */
  onPointerLeave() {
    this.thumbnail.classList.remove(CLASS_NAMES.IS_CURSOR_ACTIVE);

    return this;
  }

  /**
   * Tracks the pointer while it moves across the thumbnail.
   * @param {PointerEvent} event - The pointermove event on the thumbnail.
   * @returns {BodyVideo} A reference to the current instance
   * @chainable
   */
  onPointerMove(event) {
    if (this.isPlaying) return this;

    this.setCursorPosition(event);

    return this;
  }

  /**
   * Writes the pointer position, relative to the thumbnail, as custom
   * properties for the circle's transform to consume.
   * @param {PointerEvent} event - The pointer event carrying the coordinates.
   * @returns {BodyVideo} A reference to the current instance
   * @chainable
   */
  setCursorPosition(event) {
    const bounds = this.thumbnail.getBoundingClientRect();

    this.thumbnail.style.setProperty(
      MISC.BV_CURSOR_X,
      `${event.clientX - bounds.left}px`
    );

    this.thumbnail.style.setProperty(
      MISC.BV_CURSOR_Y,
      `${event.clientY - bounds.top}px`
    );

    return this;
  }

  /**
   * Retires the circle and restores the native cursor.
   * @returns {BodyVideo} A reference to the current instance
   * @chainable
   */
  hideCursor() {
    if (!this.thumbnail) return this;

    this.thumbnail.classList.remove(CLASS_NAMES.IS_CURSOR_ACTIVE);

    return this;
  }

  onPlay(event) {
    event.preventDefault();

    if (this.iframe) {
      if (window.Cookiebot && !window.Cookiebot.consent?.marketing) {
        return this.onCookieSettingsClick(event);
      }

      // Cookiebot may still be restoring the iframe after consent changes.
      if (!this.iframe.getAttribute('src')) return this;
    }

    this.isPlaying = true;

    this.hidePlayButton();
    this.hideCursor();
    this.activatePreview();
    this.playVideo();

    return this;
  }

  onPlayKeydown(event) {
    const isEnter = event.key === EVENTS.KEY_ENTER;

    const isSpace = event.key === EVENTS.KEY_SPACE;

    if (!isEnter && !isSpace) return this;

    this.onPlay(event);

    return this;
  }

  hidePlayButton() {
    this.playButton.style.display = 'none';

    return this;
  }

  activatePreview() {
    if (!this.preview || !this.thumbnail) return this;

    this.thumbnail.classList.add(CLASS_NAMES.IS_PLAYING);

    return this;
  }

  playVideo() {
    if (this.iframe) {
      const url = new URL(this.iframe.src);
      url.searchParams.set('autoplay', '1');
      this.iframe.setAttribute(
        'allow',
        `autoplay; ${this.iframe.getAttribute('allow') || ''}`
      );
      this.iframe.src = url.toString();
      this.iframe.focus();

      return this;
    }

    this.video.removeAttribute('poster');
    this.video.controls = true;
    this.video.style.width = '100%';
    this.video.style.height = '100%';
    this.video.play().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Video playback failed:', error);
    });

    return this;
  }

  /**
   * Tears down event listeners and clears the cursor's custom properties.
   * @returns {BodyVideo} A reference to the current instance
   * @chainable
   */
  destroy() {
    if (this.cookieSettingsButton) {
      this.cookieSettingsButton.removeEventListener(
        EVENTS.CLICK,
        this.handleCookieSettingsClick
      );
    }

    if (this.playButton) {
      this.playButton.removeEventListener(EVENTS.CLICK, this.handlePlayClick);

      this.playButton.removeEventListener(
        EVENTS.KEY_DOWN,
        this.handlePlayKeydown
      );
    }

    if (this.cursorEnabled) {
      this.thumbnail.removeEventListener(
        EVENTS.POINTERENTER,
        this.handlePointerEnter
      );

      this.thumbnail.removeEventListener(
        EVENTS.POINTERLEAVE,
        this.handlePointerLeave
      );

      this.thumbnail.removeEventListener(
        EVENTS.POINTERMOVE,
        this.handlePointerMove
      );

      this.thumbnail.style.removeProperty(MISC.BV_CURSOR_X);
      this.thumbnail.style.removeProperty(MISC.BV_CURSOR_Y);
      this.thumbnail.classList.remove(CLASS_NAMES.IS_CURSOR_ACTIVE);

      this.cursorEnabled = false;
    }

    return this;
  }
}
